import { motion, AnimatePresence } from 'framer-motion'
import type { PlayingCard as PlayingCardType } from '@/types'
import { PlayingCard } from '@/components/common/PlayingCard'
import { cn } from '@/lib/utils'

interface HandProps {
  cards: PlayingCardType[]
  value: number
  label: string
  isDealer?: boolean
  showHiddenCard?: boolean
  isWinner?: boolean
  isBusted?: boolean
  isBlackjack?: boolean
}

export function Hand({
  cards,
  value,
  label,
  isDealer = false,
  showHiddenCard = true,
  isWinner,
  isBusted,
  isBlackjack,
}: HandProps) {
  return (
    <div className="flex flex-col items-center">
      {/* Label */}
      <h3
        className={cn(
          'font-cinzel text-sm tracking-wide mb-3',
          isWinner ? 'text-casino-gold' : 'text-casino-gold/60'
        )}
      >
        {label}
      </h3>

      {/* Cards */}
      <div className="relative flex justify-center min-h-[120px]">
        <AnimatePresence>
          {cards.map((card, index) => {
            const isHidden = isDealer && index === 1 && !showHiddenCard

            return (
              <motion.div
                key={`${card.suit}${card.value}-${index}`}
                className="relative"
                style={{
                  marginLeft: index > 0 ? '-30px' : 0,
                  zIndex: index,
                }}
                initial={{ opacity: 0, y: -50, rotateY: 180 }}
                animate={{ opacity: 1, y: 0, rotateY: 0 }}
                transition={{ delay: index * 0.15, duration: 0.3 }}
              >
                {isHidden ? (
                  <div className="w-20 h-28 rounded-lg shadow-lg overflow-hidden border-2 border-casino-gold">
                    <img
                      src="/img/card-back.png"
                      alt="Hidden card"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <PlayingCard card={card} animate={false} size="lg" />
                )}
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>

      {/* Hand value */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: cards.length > 0 ? 1 : 0, scale: 1 }}
        className={cn(
          'mt-4 px-4 py-1.5 rounded-full font-bold text-lg',
          'bg-casino-black/80 border-2',
          isBusted && 'border-red-500 text-red-500',
          isBlackjack && 'border-casino-gold text-casino-gold',
          isWinner && !isBusted && !isBlackjack && 'border-green-500 text-green-500',
          !isBusted && !isBlackjack && !isWinner && 'border-casino-gold/40 text-casino-gold/80'
        )}
      >
        {isBusted ? (
          <span>BUST ({value})</span>
        ) : isBlackjack ? (
          <span>BLACKJACK!</span>
        ) : (
          <span>{value}</span>
        )}
      </motion.div>
    </div>
  )
}
